/**
 * FeatureCarousel Component
 * Auto-advancing carousel showcasing Riticket's key features
 * Slides: We are social, Discover events, Price intelligence, Play it safe
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ticketIcon from '../../../../../assets/Ticket.svg';
import crewsIcon from '../../../../../assets/Group.svg';
import bidsIcon from '../../../../../assets/bidshand.svg';
import linkUpsIcon from '../../../../../assets/twoGroup.png';
import pricesIcon from '../../../../../assets/prices.svg';
import shieldImage from '../../../../../assets/default-shield.svg';
import verifiedTicketIcon from '../../../../../assets/verified.svg';
import gearIcon from '../../../../../assets/account.svg';
import starEyesIcon from '../../../../../assets/star_eyes.svg';
import paymentsPoweredByStripeRight from '../../../../../assets/Powered_by_Stripe_right.svg';
import sammyAvatar from '../../../../../assets/sammy.png';
import joyAvatar from '../../../../../assets/Joy.png';
import jayAvatar from '../../../../../assets/Jay.png';
import silverAvatar from '../../../../../assets/silver.png';
import classes from './FeatureCarousel.module.css';

/* ───────── slide data ───────── */
const slides = [
  {
    id: 'social',
    title: 'Get',
    highlight: 'social',
    subtitle: 'Connect with real fans',
    badges: [
      { label: 'Crews', detail: 'Find your people', icon: crewsIcon, position: 'topLeft' },
      { label: 'LinkUps', detail: 'Make it a night', icon: linkUpsIcon, position: 'bottomRight' },
    ],
    visualType: 'chat',
  },
  {
    id: 'discover',
    title: 'events',
    highlight: 'Discover',
    highlightFirst: true,
    subtitle: 'Find tickets based on your taste',
    badges: [
      {
        label: 'Tickets',
        detail: 'Live events near you',
        icon: ticketIcon,
        position: 'bottomLeft',
        motion: 'pulse',
      },
      {
        label: 'Bids',
        detail: 'Name your price',
        icon: bidsIcon,
        position: 'topRight',
        motion: 'pulse',
      },
    ],
    visualType: 'phone',
  },
  {
    id: 'price',
    title: 'Track price',
    highlight: 'trends',
    subtitle: 'Real-time price intelligence',
    badges: [
      {
        label: 'Price insights',
        detail: 'Know before you go',
        icon: pricesIcon,
        position: 'bottomCenter',
      },
    ],
    visualType: 'chart',
  },
  {
    id: 'safe',
    title: 'Play it',
    highlight: 'safe',
    subtitle: 'Built-in safety & security',
    badges: [
      {
        label: 'Social proof',
        detail: 'Community-powered ratings',
        icon: starEyesIcon,
        position: 'lowerRight',
      },
      {
        label: 'Total control',
        detail: 'Protection & privacy tools',
        icon: gearIcon,
        iconTone: 'filled',
        position: 'right',
      },
      {
        label: 'Worry-free',
        detail: 'Buyer guarantee',
        icon: verifiedTicketIcon,
        position: 'left',
      },
    ],
    visualType: 'shield',
    hasStripe: true,
  },
];

/* ───────── slide variants ───────── */
const slideVariants = {
  enter: dir => ({ x: dir > 0 ? 260 : -260, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: dir => ({ x: dir > 0 ? -260 : 260, opacity: 0 }),
};

/* ───────── visual sub-components ───────── */

const ChatVisual = () => (
  <div className={classes.chatVisual}>
    <div className={classes.socialMockup}>
      <div className={classes.crewActivityCard} aria-hidden="true">
        <div className={classes.crewActivityTitle}>Crew Activity</div>
        {[
          {
            name: 'Lia',
            image: joyAvatar,
            title: 'Lia joined',
            meta: 'Beyoncé - May 14',
          },
          { name: 'Eli', image: jayAvatar, title: 'Eli posted in Crew', meta: 'MSG Floor B' },
          { name: 'Noah', image: sammyAvatar, title: 'Noah is on the way', meta: 'live thread' },
        ].map(person => (
          <div className={classes.crewActivityRow} key={person.name}>
            <div className={classes.crewActivityAvatar}>
              <img src={person.image} alt="" />
            </div>
            <div>
              <strong>
                {person.title}
                {person.status ? (
                  <span className={classes.joinedSticker}>{person.status}</span>
                ) : null}
              </strong>
              <span>{person.meta}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={classes.liveThreadCard}>
        <div className={classes.liveThreadHeader}>
          <div>
            <strong>Beyoncé - May 14</strong>
            <span>6 going · MSG Floor B</span>
          </div>
          <div className={classes.liveBadge}>LINKUP</div>
        </div>

        <div className={classes.liveMessage}>
          <div className={classes.liveAvatar}>
            <img src={sammyAvatar} alt="Noah" />
          </div>
          <div className={classes.liveMessageBody}>
            <div className={classes.liveNameLine}>
              <strong>Noah</strong>
              <span>32mins</span>
            </div>
            <p>I got the Uber. Lobby in 5 mins!</p>
            <div className={classes.reactionRow}>
              <span className={classes.reactionPill}>
                <span aria-hidden="true">💯</span> 3
              </span>
              <span className={classes.replyPill}>
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Reply
              </span>
            </div>
          </div>
        </div>

        <div className={classes.liveMessage}>
          <div className={classes.liveAvatar}>
            <img src={silverAvatar} alt="Emma" />
          </div>
          <div className={classes.liveMessageBody}>
            <div className={classes.liveNameLine}>
              <strong>Emma</strong>
              <span>17mins</span>
            </div>
            <p>silver boots &amp; bey tickets...YES</p>
            <div className={classes.reactionRow}>
              <span className={classes.reactionPill}>
                <span aria-hidden="true">🔥</span> 2
              </span>
              <span className={classes.reactionPill}>
                <span aria-hidden="true">🎟️</span> 5
              </span>
            </div>
          </div>
        </div>

        <div className={classes.liveMessage}>
          <div className={`${classes.liveAvatar} ${classes.liveAvatarLuis}`}>LU</div>
          <div className={classes.liveMessageBody}>
            <div className={classes.liveNameLine}>
              <strong>Luis</strong>
              <span>2m</span>
            </div>
            <p>I have popcorn + pretzels covered 🍿</p>
            <div className={classes.reactionRow}>
              <span className={classes.reactionPill}>🍿 3</span>
              <span className={classes.reactionPill}>🧍 2</span>
              <span className={classes.iconPill} />
            </div>
          </div>
        </div>

        <div className={classes.liveCommentInput}>
          <span>YO</span>
          <strong>Leave a comment...</strong>
          <button type="button" aria-label="Send comment">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 4 3.5 11.5l7 2 2 7L20 4Z" />
              <path d="m10.5 13.5 4.5-4.5" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
);

const PhoneVisual = () => (
  <div className={classes.phoneVisual}>
    <div className={classes.phoneMockup}>
      <div className={classes.phoneScreen}>
        <div className={classes.phoneNotch}>
          <span />
          <span />
          <span />
        </div>
        <div className={classes.showcaseCard}>
          <div className={classes.showcaseDate}>
            <span className={classes.showcaseMonth}>MAY</span>
            <strong>16</strong>
          </div>
          <div className={classes.showcaseInfo}>
            <strong>Stadium night</strong>
            <span>Crews, tickets and plans in one place</span>
          </div>
        </div>
        <div className={classes.phoneCards}>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  </div>
);

const ChartVisual = () => (
  <div className={classes.chartVisual}>
    <div className={classes.chartStage}>
      <svg
        viewBox="0 0 2048 1024"
        role="img"
        aria-label="Ticket price trend from 54 days ago to now"
        className={classes.priceTrendChart}
      >
        <defs>
          <linearGradient
            id="priceTrendFill"
            x1="0"
            y1="94"
            x2="0"
            y2="872"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#6f55ff" stopOpacity="0.34" />
            <stop offset="0.46" stopColor="#5f4ad6" stopOpacity="0.29" />
            <stop offset="1" stopColor="#5a43d2" stopOpacity="0.50" />
          </linearGradient>
          <pattern id="priceTrendTexture" width="1" height="7" patternUnits="userSpaceOnUse">
            <path d="M0 0H2048" stroke="#302862" strokeOpacity="0.26" strokeWidth="1" />
          </pattern>
          <filter id="priceTrendGlow" x="-4%" y="-6%" width="108%" height="112%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <clipPath id="priceTrendAreaClip">
            <path d="M136 739C156 762 180 776 211 783C239 789 267 784 290 786C316 788 334 812 359 829C386 846 410 830 433 821C458 810 482 827 508 836C537 846 561 835 582 807C606 773 620 735 646 750C688 774 719 752 749 745C784 736 826 778 852 781C881 784 896 764 914 714C927 679 938 587 951 480C966 346 981 163 1009 119C1024 96 1046 94 1059 128C1083 189 1091 328 1110 446C1129 567 1152 649 1181 681C1215 719 1242 654 1260 622C1272 600 1290 616 1305 661C1318 698 1323 750 1352 766C1375 779 1391 712 1412 684C1436 652 1480 692 1507 699C1536 706 1558 674 1587 674C1616 674 1631 728 1652 760C1675 794 1700 776 1722 781C1746 786 1758 762 1781 788C1801 811 1830 794 1854 762C1875 734 1879 688 1904 642C1927 600 1957 641 1978 679C1989 700 1995 739 1996 785L1996 872L136 872Z" />
          </clipPath>
        </defs>

        <rect width="2048" height="1024" fill="#070711" />
        <g className={classes.chartGrid}>
          <path d="M119 170H2030" />
          <path d="M119 324H2030" />
          <path d="M119 476H2030" />
          <path d="M119 629H2030" />
          <path d="M119 781H2030" />
        </g>
        <g className={classes.chartLabels}>
          <text x="22" y="181">
            $350
          </text>
          <text x="22" y="335">
            $300
          </text>
          <text x="22" y="487">
            $250
          </text>
          <text x="22" y="640">
            $200
          </text>
          <text x="22" y="792">
            $150
          </text>
          <text x="52" y="984">
            54 days ago
          </text>
          <text x="1942" y="984">
            Now
          </text>
        </g>
        <path
          d="M136 739C156 762 180 776 211 783C239 789 267 784 290 786C316 788 334 812 359 829C386 846 410 830 433 821C458 810 482 827 508 836C537 846 561 835 582 807C606 773 620 735 646 750C688 774 719 752 749 745C784 736 826 778 852 781C881 784 896 764 914 714C927 679 938 587 951 480C966 346 981 163 1009 119C1024 96 1046 94 1059 128C1083 189 1091 328 1110 446C1129 567 1152 649 1181 681C1215 719 1242 654 1260 622C1272 600 1290 616 1305 661C1318 698 1323 750 1352 766C1375 779 1391 712 1412 684C1436 652 1480 692 1507 699C1536 706 1558 674 1587 674C1616 674 1631 728 1652 760C1675 794 1700 776 1722 781C1746 786 1758 762 1781 788C1801 811 1830 794 1854 762C1875 734 1879 688 1904 642C1927 600 1957 641 1978 679C1989 700 1995 739 1996 785L1996 872L136 872Z"
          fill="url(#priceTrendFill)"
        />
        <rect
          width="2048"
          height="1024"
          fill="url(#priceTrendTexture)"
          clipPath="url(#priceTrendAreaClip)"
        />
        <path
          d="M136 739C156 762 180 776 211 783C239 789 267 784 290 786C316 788 334 812 359 829C386 846 410 830 433 821C458 810 482 827 508 836C537 846 561 835 582 807C606 773 620 735 646 750C688 774 719 752 749 745C784 736 826 778 852 781C881 784 896 764 914 714C927 679 938 587 951 480C966 346 981 163 1009 119C1024 96 1046 94 1059 128C1083 189 1091 328 1110 446C1129 567 1152 649 1181 681C1215 719 1242 654 1260 622C1272 600 1290 616 1305 661C1318 698 1323 750 1352 766C1375 779 1391 712 1412 684C1436 652 1480 692 1507 699C1536 706 1558 674 1587 674C1616 674 1631 728 1652 760C1675 794 1700 776 1722 781C1746 786 1758 762 1781 788C1801 811 1830 794 1854 762C1875 734 1879 688 1904 642C1927 600 1957 641 1978 679C1989 700 1995 739 1996 785"
          fill="none"
          stroke="#7c5cff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="7"
          filter="url(#priceTrendGlow)"
        />
        <circle cx="1996" cy="785" r="20" fill="#7c5cff" />
        <circle
          cx="1996"
          cy="785"
          r="22"
          fill="none"
          stroke="#7c5cff"
          strokeOpacity="0.20"
          strokeWidth="16"
        />
      </svg>

      <div className={classes.chartInsightCard}>
        <div className={classes.insightRange}>
          <div className={classes.insightPriceBubble}>$245</div>
          <div className={classes.insightTrack}>
            <span />
          </div>
          <div className={classes.insightRangeLabels}>
            <span>$64 - great</span>
            <span>ouch - $380</span>
          </div>
        </div>
        <div className={classes.insightBody}>
          <div className={classes.insightIcon}>→</div>
          <div>
            <strong>Fair market price</strong>
            <span>
              Right around the 30-day average. No urgency to buy or wait — this is a normal price
              for this event.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ShieldVisual = () => (
  <div className={classes.shieldVisual}>
    <div className={classes.shieldIcon}>
      <img src={shieldImage} alt="Safety shield" className={classes.shieldImage} />
    </div>
    <a
      className={classes.stripeBadge}
      href="https://stripe.com/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Secure payments powered by Stripe"
    >
      <img
        src={paymentsPoweredByStripeRight}
        alt=""
        className={classes.stripeBadgeImage}
        aria-hidden="true"
        draggable="false"
      />
    </a>
  </div>
);

/* ───────── visual map ───────── */
const visualComponents = {
  chat: ChatVisual,
  phone: PhoneVisual,
  chart: ChartVisual,
  shield: ShieldVisual,
};

/* ───────── main component ───────── */
const FeatureCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goToSlide = useCallback(
    index => {
      setDirection(index > activeIndex ? 1 : -1);
      setActiveIndex(index);
    },
    [activeIndex]
  );

  const nextSlide = useCallback(() => {
    setDirection(1);
    setActiveIndex(prev => (prev + 1) % slides.length);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide]);

  // Touch handlers
  const handleTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = e => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setDirection(1);
        setActiveIndex(prev => (prev + 1) % slides.length);
      } else {
        setDirection(-1);
        setActiveIndex(prev => (prev - 1 + slides.length) % slides.length);
      }
    }
  };

  const currentSlide = slides[activeIndex];
  const Visual = visualComponents[currentSlide.visualType];

  return (
    <div
      className={`${classes.showcase} ${classes[`showcase_${currentSlide.id}`] || ''}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Inner glass panel */}
      <div className={classes.innerPanel}>
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentSlide.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
            className={`${classes.slide} ${classes[`slide_${currentSlide.id}`] || ''}`}
          >
            <h2 className={classes.slideTitle}>
              {currentSlide.highlightFirst ? (
                <>
                  <span className={classes.slideHighlight}>{currentSlide.highlight}</span>{' '}
                  {currentSlide.title}
                </>
              ) : (
                <>
                  {currentSlide.title}{' '}
                  <span className={classes.slideHighlight}>{currentSlide.highlight}</span>
                </>
              )}
            </h2>
            <p className={classes.slideSubtitle}>{currentSlide.subtitle}</p>

            <div className={classes.visualArea}>
              <Visual />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Floating badges — on the showcase level for overlap */}
      {currentSlide.badges.map((badge, index) => {
        const badgeShellClass = badge.motion === 'spinExpand' ? classes.badgeSpinExpandShell : '';
        const motionClass =
          badge.motion === 'spinExpand'
            ? ''
            : badge.motion === 'pulse' || index % 3 === 0
            ? classes.badgeMotionPulse
            : index % 3 === 1
            ? classes.badgeMotionSpin
            : classes.badgeMotionDepth;

        return (
          <div
            key={badge.label}
            className={`${classes.badge} ${classes[`badge_${badge.position}`]} ${badgeShellClass}`}
          >
            <div className={`${classes.badgeMotion} ${motionClass}`}>
              {badge.icon && badge.iconTone === 'filled' && (
                <span
                  className={`${classes.badgeIcon} ${classes.badgeIconFilled}`}
                  style={{ '--badgeIconUrl': `url(${badge.icon})` }}
                  aria-hidden="true"
                />
              )}
              {badge.icon && badge.iconTone !== 'filled' && (
                <img src={badge.icon} alt="" className={classes.badgeIcon} />
              )}
              {!badge.icon && <div className={classes.badgeEmoji}>😈</div>}
              <span className={classes.badgeText}>
                <strong>{badge.label}</strong>
                <small>{badge.detail}</small>
              </span>
            </div>
          </div>
        );
      })}

      {/* Dot navigation — absolute at bottom */}
      <div className={classes.dots}>
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            className={`${classes.dot} ${i === activeIndex ? classes.dotActive : ''}`}
            onClick={() => goToSlide(i)}
            aria-label={`Go to slide ${i + 1}: ${slide.title} ${slide.highlight}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeatureCarousel;
